import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { removeParameters } from '@/app/common/urlParametersUtils.ts';
import { evoFiltersEnabled } from '@/app/common/filters/core/filterFlags.ts';
import { asLegacyRatingValue, evoSelectedRating } from '@/app/common/filters/core/ratingValues.ts';
import { EVO_STARS_VALUES, NO_STAR_RATING_VALUE } from '@/app/common/filters/core/starsValues.ts';
import { urlParamOf } from '@/app/common/filters/core/filterDefinitions.ts';
import type { FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';
import type { FilterAvailabilityContext } from '@/app/common/filters/groups/groupDescriptors.ts';
import { FILTER_GROUPS } from '@/app/common/filters/groups/groupDescriptors.ts';

export const unavailableFilterNames = (context: FilterAvailabilityContext): FILTER_NAME[] =>
  FILTER_GROUPS.flatMap((descriptor) => [
    ...(descriptor.available && !descriptor.available(context) ? (descriptor.filterNames ?? []) : []),
    ...(descriptor.unavailable?.(context) ?? []),
  ]);

export const filterAvailability = (context: FilterAvailabilityContext): ((name: FILTER_NAME) => boolean) => {
  const unavailable = new Set(unavailableFilterNames(context));

  return (name) => !unavailable.has(name);
};

const unavailableFilterParams = (context: FilterAvailabilityContext): string[] =>
  unavailableFilterNames(context)
    .map(urlParamOf)
    .filter((param): param is NonNullable<typeof param> => !!param);

const withOfferedValue = (
  searchParams: SearchParams,
  param: URLParameters,
  offered: string | undefined
): SearchParams => {
  if (searchParams[param] === offered) return searchParams;

  return offered ? { ...searchParams, [param]: offered } : removeParameters(searchParams, [param]);
};

// Each layout names the buckets its own way, so a url from the other is translated here. Evo is
// single choice and drops what it cannot offer; the TSX filters take every bucket.
const strippedRatingValue = (searchParams: SearchParams, context: FilterAvailabilityContext): SearchParams => {
  const rating = searchParams[URLParameters.RATING];
  if (rating === undefined) return searchParams;

  const offered = evoFiltersEnabled(context.localization?.configValuesApp)
    ? evoSelectedRating(rating)
    : [...new Set(rating.split(',').map(asLegacyRatingValue))].join(',');

  return withOfferedValue(searchParams, URLParameters.RATING, offered);
};

const EVO_OFFERED_STARS = [...EVO_STARS_VALUES, NO_STAR_RATING_VALUE];

const strippedStarsValue = (searchParams: SearchParams, context: FilterAvailabilityContext): SearchParams => {
  const stars = searchParams[URLParameters.STARS];
  if (stars === undefined || !evoFiltersEnabled(context.localization?.configValuesApp)) return searchParams;

  const offered = stars
    .split(',')
    .filter((value) => EVO_OFFERED_STARS.includes(value))
    .join(',');

  return withOfferedValue(searchParams, URLParameters.STARS, offered);
};

export const stripUnavailableFilterParams = (
  searchParams: SearchParams,
  context: FilterAvailabilityContext
): SearchParams => {
  const withOfferedValues = strippedStarsValue(strippedRatingValue(searchParams, context), context);
  const unavailable = new Set(unavailableFilterParams(context));
  if (!unavailable.size) return withOfferedValues;

  const entries = Object.entries(withOfferedValues);
  const kept = entries.filter(([key]) => !unavailable.has(key));
  if (kept.length === entries.length) return withOfferedValues;

  return Object.fromEntries(kept) as SearchParams;
};
